import {EventEmitter, Injectable} from '@angular/core';
import { BehaviorSubject, combineLatest, delay, filter, first, map, Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {ActionResponse} from '../../../../shared/_models/action-response.model';
import {ActionService} from '../../../../shared/_services/action.service';
import {DSTEntityDetails} from './templates/template-leid-selector/template-leid-selector.component';
import {DSTDynamicComponentResolverService, DSTSavePayload} from './dst-dynamic-component-resolver.service';
import {
  CreateDisclosureTemplateViewportComponent
} from './create-disclosure-template-viewport/create-disclosure-template-viewport.component';
import {TrackerTabsService, TrackerTabState} from '../../tracker-tabs.service';
import {CreateDisclosureTemplateHostDirective} from './create-disclosure-template-viewport/create-disclosure-template-host.directive';
import {DSTFormState, StmtData} from '../../../shared/models/dst-form-state.model';
import {DSTSharedService} from '../../../shared/dst-shared.service';

@Injectable({
  providedIn: 'root'
})
export class CreateDisclosureSharedService {

  // savedStatementId: string = '';
  csStatementId!: number;
  leid: string = '';
  // sharedData: StmtData | {} = {}; // is currently used to temporarily store form data during the process of one active form tab (inner tab) transition from view mode to edit mode
  disableActions: boolean = false;

  private secondaryAttachments$ = new BehaviorSubject<any[]>([]);
  private searchAttachments$ = new BehaviorSubject<string>('');

  constructor(private actionService: ActionService,
              private dCRService: DSTDynamicComponentResolverService,
              private httpClient: HttpClient,
              private dstSharedService: DSTSharedService,
              private trackerTabsService: TrackerTabsService) {
  }

  setActiveIndex(index: number): void {
    this.getActiveTrackerTab().activeTabIndex = index;
  }

  getActiveIndex(): number {
    return this.getActiveTrackerTab().activeTabIndex || 0;
  }

  setSavedStatementId(statementId: string): void {
    this.getActiveFormState().statementId = statementId;
  }

  setCSStatementId(csStatementId: number): void {
    this.csStatementId = csStatementId;
  }

  setLeId(leid: string): void {
    this.leid = leid;
  }

  private getActiveTrackerTab(): TrackerTabState {
    const activeTrackerTab = this.trackerTabsService.getActiveTrackerTab();
    if (!activeTrackerTab) {
      throw Error('Could not find active tracker tab!');
    }
    return activeTrackerTab;
  }

  setFormData(data: StmtData, formId?: number): void {
    const formState = formId === undefined
      ? this.getActiveFormState()
      : this.getFormStateByFormId(formId);
    if (!formState) {
      return;
    }
    this.ensureFormStateTaxYear(formState, data);
    formState.setFormData(data);
  }

  getFormData(formId?: number): StmtData | undefined {
    if (formId === undefined) {
      return this.getActiveFormState().getFormData();
    }
    return this.getFormStateByFormId(formId)?.formData;
  }

  getFormStateByFormId(formId: number): DSTFormState | undefined {
    return this.getActiveTrackerTab().getFormStateByFormId(formId);
  }

  getFormStateArray(): DSTFormState[] {
    return this.getActiveTrackerTab().formStateArray;
  }

  getActiveFormState(): DSTFormState {
    return this.getActiveTrackerTab().getActiveFormState();
  }

  /**
   * Tax year to send with statement requests, as a string.
   *
   * A form state is not guaranteed to carry a tax year: child statements are built
   * from a save response that only identifies them, and statements reached through
   * navigation are built from row data that may not include TAXYEAR. The statement
   * that is loaded in the template always knows its own tax year, so callers pass
   * it in as the fallback instead of dereferencing a possibly missing value.
   */
  getTaxYearStr(stmtData?: StmtData): string {
    const formState = this.getActiveFormState();
    this.ensureFormStateTaxYear(formState, stmtData);
    const taxYear = formState?.taxYear
      ?? this.readStatementTaxYear(stmtData)
      ?? this.readStatementTaxYear(formState?.getFormData())
      ?? this.dstSharedService.gtwTaxYear;
    return taxYear?.toString() ?? '';
  }

  /**
   * Child statements and some navigation rows are created without a tax year.
   * Copy it from the loaded statement so later reads do not call toString on undefined.
   */
  private ensureFormStateTaxYear(formState: DSTFormState | undefined, data?: StmtData): void {
    if (!formState) {
      return;
    }
    if (formState.taxYear == null) {
      const year = this.readStatementTaxYear(data)
        ?? this.readStatementTaxYear(formState.getFormData())
        ?? this.dstSharedService.gtwTaxYear;
      const parsedYear = Number(year);
      if (year != null && year !== '' && !Number.isNaN(parsedYear)) {
        formState.taxYear = parsedYear;
      }
    }
    if (formState.taxYearId == null && data) {
      const raw = data as StmtData & { TAXYEARID?: string | number; taxYearId?: string | number };
      const yearId = raw.taxyearid ?? raw.TAXYEARID ?? raw.taxYearId;
      const parsedYearId = Number(yearId);
      if (yearId != null && yearId !== '' && !Number.isNaN(parsedYearId)) {
        formState.taxYearId = parsedYearId;
      }
    }
  }

  private readStatementTaxYear(data?: StmtData): string | number | undefined {
    if (!data) {
      return undefined;
    }
    const raw = data as StmtData & { TAXYEAR?: string | number; taxYear?: string | number; TAX_YEAR?: string | number };
    return raw.taxyear ?? raw.TAXYEAR ?? raw.taxYear ?? raw.TAX_YEAR;
  }

  setTemplateHost(host: CreateDisclosureTemplateHostDirective, index: number): void {
    this.getActiveTrackerTab().setTemplateHost(host, index);
  }

  openChildStatements(childStatements: any[], onSaveEmitter: EventEmitter<DSTSavePayload>): void {
    const childFormStates: DSTFormState[] = [];
    // The save response only identifies the generated children, so tax year and
    // return period are inherited from the parent that produced them (they always
    // belong to the same filing). Without this the child form state carries an
    // undefined taxYear, which breaks every template that reads it.
    const parentFormState: DSTFormState | undefined = this.getActiveFormState();
    // this.disableActions = true;
    this.switchTemplateMode(true);
    childStatements.forEach((child: any) => {
      childFormStates.push(new DSTFormState(child.taxYear ?? parentFormState?.taxYear,
        child.taxYearId ?? parentFormState?.taxYearId,
        child.returnPeriodId ?? parentFormState?.returnPeriodId,
        child.statementId, child.formId, child.statementName, '', child.formTypeId, child));
    });
    // prevent generating child statements if already exist & redirect to first child
    const redirectToChild = (this.getFormStateArray().length > 1);
    this.trackerTabsService.setActiveTrackerTabChildIndex(1);
    // open child statement - see create-disclosure-shared.component.ts for details
    setTimeout(() => {
      try {
        // Check if the EventEmitter is still active before emitting
        if (onSaveEmitter && !onSaveEmitter.closed) {
          onSaveEmitter.emit({trackerTab: this.getActiveTrackerTab(), childFormStates: childFormStates, redirectToChild: redirectToChild});
        } else {
          console.warn('EventEmitter is closed or undefined, skipping emit');
        }
        // redirectToChild && (childStatements.length > 1) && this.trackerTabsService.setActiveTrackerTabChildIndex(2); //update child index after redirecting to first child
      } catch (error) {
        console.error('Error emitting onSave event:', error);
      }
    });
  }

  switchTemplateMode(isView?: boolean): void {
    const activeHost = this.getActiveTrackerTab().getActiveTemplateHost();
    const activeFormState = this.getActiveTrackerTab().getActiveFormState();
    this.switchTemplate(activeHost, activeFormState, isView);
  }

  private switchTemplate(activeHost: CreateDisclosureTemplateHostDirective, activeFormState: DSTFormState, isView?: boolean): void {
    if (!activeHost) {
      console.error('Active template host is missing, unable to proceed!');
      return;
    }
    if (!activeFormState) {
      console.error('No shared form state, unable to proceed!');
      return;
    }
    activeFormState.isView = !!isView;
    activeFormState.setDoPDFSync();
    activeHost.viewContainerRef.clear();
    this.dCRService.resolve(activeHost.viewContainerRef, {
      loadChildren: CreateDisclosureTemplateViewportComponent.getComponent(
        activeFormState.formTypeId,
        activeFormState.formId,
        activeFormState.uploadType,
        activeFormState.taxYear,
        isView)
    });
  }

  redirectToNextChild(index: number | null, isView?: boolean): void {
    if (!index) {
      console.error('Redirection index is missing, unable to proceed!');
      return;
    }
    const activeHost = this.getActiveTrackerTab().templateHostArray[index];
    const activeFormState = this.getActiveTrackerTab().formStateArray[index];
    this.switchTemplate(activeHost, activeFormState, isView);
  }

  resetAppkitDropdown(list: { selected?: boolean }[]): void {
    (list || []).forEach((d) => {
      d.selected = false;
    });
  }

  /**
   *  If any of the input entities is/was a DRE, then prompt user for a confirmation
   *  with an appropriate message.
   *  @return true if user confirms and wishes to proceed, or if none of the input entities is/was a DRE,
   *         otherwise returns false
   */
  confirmDRE(entities: (DSTEntityDetails | null | undefined)[]): boolean {
    for (let i = 0; i < entities.length; i++) {
      if (!entities[i]) {
        continue;
      }

      let msg: string = '';
      let commonMSG: string = ` Entity selected was a Disregarded Entity(DRE) from ${entities[i]!.DRESTARTDATEFORMATTED} to ${entities[i]!.DREYEARENDFORMATTED}.`;
      let isDre: boolean = entities[i]!.USTAXTYPE == 'TXN';
      let wasDre: boolean = entities[i]!.DREUSTAXTYPE == 'TXN';
      if (entities[i]!.DREYEAREND) {
        commonMSG += ' For US Tax Reporting, this entity has a taxable year end of: ' + entities[i]!.DREYEARENDFORMATTED + '.';
      } else {
        commonMSG += ' Year end information not found for this entity.';
      }
      if (isDre) {
        msg = 'You have selected an entity (' + entities[i]!.LEID + ') that is disregarded(DRE) for US Tax purposes. Are you sure you want to continue saving?' + commonMSG;
      } else if (wasDre) {
        msg = 'You have selected an entity (' + entities[i]!.LEID + ') that was disregarded(DRE) for US Tax purposes at some point during this tax year. ' + commonMSG;
      }
      if (isDre || wasDre) {
        return window.confirm(msg);
      }
    }

    return true;
  }

  /**
   *  Check if any of the input entities is/was a DRE.
   *  @return a composed string message to the caller, or an empty string if none is/was a DRE.
   */
  areEntitiesDisregarded(entities: (DSTEntityDetails | null | undefined)[]): string {
    for (let i = 0; i < entities.length; i++) {
      const DREMsg: string = this.buildDREMsg(entities[i]);
      if (DREMsg !== '') {
        return DREMsg;
      }
    }
    return '';
  }

  private buildDREMsg(entity: DSTEntityDetails | null | undefined): string {
    if (!entity) {
      return '';
    }

    let msg: string = '';
    let commonMSG: string = `Entity selected was a Disregarded Entity(DRE) from ${entity.DRESTARTDATEFORMATTED} to ${entity.DREYEARENDFORMATTED}.`;
    let wasDre: boolean = entity.DREUSTAXTYPE == 'TXN';
    if (entity.DREYEAREND) {
      commonMSG += ' For US Tax Reporting, this entity has a taxable year end of: ' + entity.DREYEARENDFORMATTED;
    } else {
      commonMSG += ' Year end information not found for this entity';
    }
    if (entity.USTAXTYPE == 'TXN') {
      msg = 'You have selected an entity (' + entity.LEID + ') that is disregarded(DRE) for US Tax purposes. ' + commonMSG + '.  Please ensure that the statement is prepared using the appropriate regarded US Entity.';
    } else {
      if (wasDre) {
        msg = 'You have selected an entity (' + entity.LEID + ') that was disregarded(DRE) for US Tax purposes at some point during this tax year. ' + commonMSG + '.  Please make sure that the appropriate regarded entity is selected for the time period of the transaction.';
      } else {
        return '';
      }
    }

    return msg;
  }

  concatLEIDs(entities: DSTEntityDetails[]): string {
    let result: string = '';
    entities.forEach((entity: DSTEntityDetails) => {
      if (!entity) {
        return;
      }
      result += entity.LEID + ',';
    });
    // remove last comma
    if (result) {
      result = result.substring(0, result.length - 1);
    }
    return result;
  }

  getTransDateStr(transDate: string | Date | { startDate: any, endDate: any }, minusDays: number = 0): string {
    if (transDate == null || transDate === '') {
      return '';
    }

    if (typeof transDate === 'string') {
      const trimmed = transDate.trim();
      if (!trimmed) {
        return '';
      }
      const date = new Date(trimmed);
      if (isNaN(date.getTime())) {
        return '';
      }
      date.setDate(date.getDate() - minusDays);
      return date.toISOString().split('T')[0];
    } else if (transDate instanceof Date) {
      if (isNaN(transDate.getTime())) {
        return '';
      }
      const date = new Date(transDate);
      date.setDate(date.getDate() - minusDays);
      return date.toISOString().split('T')[0];
    } else if (transDate?.startDate) {
      const startDate = transDate.startDate;
      if (typeof startDate.clone === 'function' && typeof startDate.subtract === 'function') {
        const date = startDate.clone();
        return date.subtract(minusDays, 'days').format('YYYY-MM-DD');
      }
      if (typeof startDate.format === 'function') {
        const formatted = startDate.format('YYYY-MM-DD');
        if (!formatted) {
          return '';
        }
        if (minusDays === 0) {
          return formatted;
        }
        const date = new Date(formatted);
        if (isNaN(date.getTime())) {
          return '';
        }
        date.setDate(date.getDate() - minusDays);
        return date.toISOString().split('T')[0];
      }
    }

    return '';
  }

  updateEntityName(leid: string, newName: string | undefined, entities: DSTEntityDetails[]
    , taxTypeCode: string | undefined): void {

    entities.forEach((entity: DSTEntityDetails) => {
      if (entity?.LEID === leid) {
        (newName) && (entity.ENTITY = newName);
        entity.TAXTYPECODE = taxTypeCode!;
        return;
      }
    });
  }

  checkForEntityNameChange(statementID: string = '0', leid: string, transDate: string | Date | { startDate: any, endDate: any }, taxYear: string): Observable<EntityNameChange[]> {
    const params = new HttpParams({
      fromObject: {
        'statement_id': statementID,
        'leid': leid,
        'transDate': this.getTransDateStr(transDate),
        'tax_year': taxYear
      }
    });
    const actionKey = 32222;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            return data.jsonObject;
          } else {
            throw Error('Something went wrong while getting the entity name, please try again!');
          }
        })
      );
  }

  getEntityList(searchTerm: string, isBranchEntity: boolean = false): Observable<DSTEntityDetails[]> {
    const params = new HttpParams({
      fromObject: {
        'tax_year': this.getTaxYearStr(),
        'srchText': searchTerm
      }
    });
    const actionKey = isBranchEntity ? 32206 : 32196;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            return data.jsonObject;
          } else {
            throw Error('Something went wrong while retrieving the entity list, please try again!');
          }
        })
      );
  }

  getDSTTaxTypesDesc(): Observable<any[]> {
    const params = new HttpParams({
      fromObject: {
        'sso_id': this.dstSharedService.user?.ssoId
      }
    });
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, 32705, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            return data.jsonObject;
          } else {
            throw Error('Something went wrong while retrieving the entity list, please try again!');
          }
        })
      );
  }

  checkfor332To926AutoGenStmt(primaryLEID: string, secondaryLEID: string, taxYear: string): Observable<ActionResponse> {
    const params = new HttpParams({
      fromObject: {
        primaryLEID,
        secondaryLEID,
        'tax_year': taxYear
      }
    });
    const actionKey = 32413;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            return data.jsonObject;
          } else {
            throw Error('Something went wrong while retrieving the 332 to 926 auto-generated disclosure details, please try again!');
          }
        })
      );
  }

  saveStatemenet(paramsObject: { [param: string]: string }, jsonObject: any, attachmentFile?: any): Observable<ActionResponse | 0> {
    const params = new HttpParams({
      fromObject: paramsObject
    });
    const clientKey = this.dstSharedService.clientKey;
    const url = `${this.dstSharedService.baseURLs.admin}/saveStatement`;
    const headers = new HttpHeaders();
    headers.append('client_key', clientKey.toString());

    const formData: FormData = new FormData();
    attachmentFile && formData.append('file', attachmentFile);
    formData.append('jsonObject', JSON.stringify(jsonObject));

    return this.httpClient.post<any>(url, formData, {
      withCredentials: true, params: params, headers: headers
    }).pipe(
      map((data: ActionResponse) => {
        if (data.callSuccess !== '1') {
          this.dstSharedService.showAlert('error', 'Error Saving Form!');
          return 0;
        } else {
          return data;
        }
      }),
      // delay(4000) // currently, the save api returns before data is actually updated in database table, remove this line once the issue is resolved
    );
  }

  saveAttachedStmt(taxYear: number,
                   taxYearId: number,
                   returnPeriodId: number,
                   dcnId: number,
                   formId: number,
                   statementID: string,
                   primaryEntityId: number,
                   branchEntityId: number | null,
                   formTypeId: number,
                   placeholderChecked: boolean,
                   attachmentFileName: string,
                   attachmentFile?: File,
                   csStatementId?: string,
                   displayContent?: string
  ): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('taxYear', taxYear.toString());
    formData.append('taxYearId', taxYearId.toString());
    formData.append('returnPeriodId', returnPeriodId.toString());
    formData.append('dcnId', dcnId.toString());
    formData.append('formId', formId.toString());
    formData.append('primaryEntityId', primaryEntityId.toString());
    branchEntityId && formData.append('branchEntityId', branchEntityId.toString());
    formData.append('formTypeId', formTypeId.toString());
    formData.append('placeHolder', placeholderChecked ? '1' : '0');
    formData.append('attachmentFileName', attachmentFileName);
    attachmentFile && formData.append('attachmentPath', attachmentFile);
    formData.append('csStatementId', csStatementId || '0');
    formData.append('inputContent', displayContent || '');
    formData.append('client_key', this.dstSharedService.clientKey.toString());
    formData.append('stmtId', statementID.toString());

    return this.httpClient.post(this.dstSharedService.baseURLs.admin + '/saveAttachedStmtByBulkUpload', formData)
      .pipe(map((data: any) => {
        if (data.callSuccess === '1') {
          return {statementId: data.statementId.toString(), userName: data.userName};
        } else {
          throw Error('Something went wrong while saving the attached statement, please try again!');
        }
      }));
  }

  getBasisDropdown(): Observable<DropDownType[]> {
    const params = new HttpParams({
      fromObject: {}
    });
    const actionKey = 32185;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            return data.jsonObject;
          } else {
            throw Error('Something went wrong while retrieving the entity details, please try again!');
          }
        })
      );
  }

  getCountryDropdown(is8621: boolean = false): Observable<DropDownType[]> {
    const params = new HttpParams({
      fromObject: {
        'sso_id': this.dstSharedService.user?.ssoId,
        'tax_year': this.getTaxYearStr(),
      }
    });
    const actionKey = is8621 ? 32419 : 32301;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            return data.jsonObject;
          } else {
            throw Error('Something went wrong while retrieving the entity details, please try again!');
          }
        })
      );
  }

  getStockTypeListDropdown(): Observable<DropDownType[]> {
    const params = new HttpParams({
      fromObject: {}
    });
    const actionKey = 32188;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            return data.jsonObject;
          } else {
            throw Error('Something went wrong while retrieving the Stock Types details, please try again!');
          }
        })
      );
  }

  getCurrencyDropdown(): Observable<DropDownType[]> {
    const params = new HttpParams({
      fromObject: {}
    });
    const actionKey = 32187;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            return data.jsonObject;
          } else {
            throw Error('Something went wrong while retrieving the entity details, please try again!');
          }
        })
      );
  }

  getStateDropdown(): Observable<DropDownType[]> {
    const params = new HttpParams({
      fromObject: {
        'sso_id': this.dstSharedService.user?.ssoId,
      }
    });
    const actionKey = 32319;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            return data.jsonObject;
          } else {
            throw Error('Something went wrong while retrieving the entity details, please try again!');
          }
        })
      );
  }

  getLiquidationStateDropdown(pickListId: number): Observable<DropDownType[]> {
    const params = new HttpParams({
      fromObject: {
        'sso_id': this.dstSharedService.user?.ssoId,
        'pick_list_id': '' + pickListId,
      }
    });
    const actionKey = 32212;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            return data.jsonObject;
          } else {
            throw Error('Something went wrong while retrieving the entity details, please try again!');
          }
        })
      );
  }

  getImport(LEID: string | undefined, taxyear: number, date: string): Observable<any> {
    let leid = '';
    if (LEID !== undefined) {
      leid = LEID;
    }
    const params = new HttpParams({
      fromObject: {
        'sso_id': this.dstSharedService.user?.ssoId,
        'tax_year': taxyear.toString(),
        'LEID': `${leid}`,
        'trans_date': date.toString(),
      }
    });
    const actionKey = 32213;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            if (data.jsonObject.length) {
              return data.jsonObject;
            }
            this.dstSharedService.showAlert('error', 'No TB amounts present for this entity');
            return data.jsonObject;
          } else {
            throw Error('Something went wrong while retrieving the entity details, please try again!');
          }
        })
      );
  }

  getLoadStatementData(formId: number = this.getActiveFormState().formId): Observable<StmtData> {
    const formState: DSTFormState | undefined = this.getFormStateByFormId(formId);
    if (!formState) {
      throw Error('Form state not found!');
    }
    const params = new HttpParams({
      fromObject: {
        'sso_id': this.dstSharedService.user?.ssoId,
        'stmt_id': formState.statementId,
      }
    });
    const actionKey = 32209;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            try {
              return JSON.parse(data.jsonObject[0].STATEMENTDATA);
            } catch (e: any) {
              if (e instanceof SyntaxError) {
                console.log('JSON parse Syntax Error' + e);
              }
              throw Error('Something went wrong while retrieving the statement data, please try again!');
            }
          } else {
            throw Error('Something went wrong while retrieving the statement data, please try again!');
          }
        })
      );
  }

  getDSTEntityInfo(entity_id: string): Observable<dstLookupEntity[]> {
    const params = new HttpParams({
      fromObject: {
        'sso_id': this.dstSharedService.user?.ssoId,
        'entity_id': entity_id,
      }
    });
    const actionKey = 32227;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            return data.jsonObject;
          } else {
            throw Error('Something went wrong while retrieving the entity details, please try again!');
          }
        })
      );
  }


  getStockType(): Observable<any> {
    let leid = '';
    const params = new HttpParams({
      fromObject: {
        'sso_id': this.dstSharedService.user?.ssoId,
      }
    });
    const actionKey = 32188;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            return data.jsonObject;
          } else {


            throw Error('Something went wrong while retrieving the entity details, please try again!');
          }
        })
      );
  }

  checkImportBtnRequired(leid: string, taxYear: string, csStatementId: string): Observable<boolean> {
    const params = new HttpParams({
      fromObject: {
        'sso_id': this.dstSharedService.user?.ssoId,
        'leid': leid,
        'tax_year': taxYear,
        'cs_stmt_id': csStatementId,
      }
    });
    const actionKey = 32254;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            return data.jsonObject[0]['IMPORTFLAG'];
          } else {
            throw Error('Something went wrong while retrieving the entity details, please try again!');
          }
        })
      );
  }

  saveHTMLToPDFStatement(HtmlContent: string, fileName: string, statementId: number): Observable<ActionResponse> {
    const params = new HttpParams()
      .set('fileName', fileName)
      .set('statementId', statementId.toString());

    const headers = new HttpHeaders();
    headers.append('client_key', this.dstSharedService.clientKey.toString());
    const url = `${this.dstSharedService.baseURLs.admin}/saveHTMLToPDFStatement`;

    return this.httpClient.post<any>(url, HtmlContent, {
      withCredentials: true, params: params, headers: headers
    }).pipe(
      map((data: ActionResponse) => {
        if (data.callSuccess !== '1') {
          throw Error('Something went wrong while saving Form as PDF in s3, please try again!');
        } else {
          return data;
        }
      }),
    );
  }

  getPdfS3Url(statementId:number): Observable<string> {
    const params = new HttpParams({
      fromObject: {
        'sso_id': this.dstSharedService.user?.ssoId,
        'statement_id': statementId.toString(),
      }
    });
    const actionKey = 32730;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            return data.jsonObject[0]['S3_URL'];
          } else {
            throw Error('Something went wrong while retrieving the entity details, please try again!');
          }
        })
      );
  }

  getEfileAttribsByStatementId(statementId:number): Observable<boolean> {
    const params = new HttpParams({
      fromObject: {
        'sso_id': this.dstSharedService.user?.ssoId,
        'statement_id': statementId.toString(),
      }
    });
    const actionKey = 32728;
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, actionKey, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            return data.jsonObject[0];
          } else {
            throw Error('Something went wrong while retrieving the entity details, please try again!');
          }
        })
      );
  }

  getStyleSheetHTML( formId: string, submissionId: string): Observable<string> {
    const params = new HttpParams()
      .set('formId', formId)
      .set('submissionId', submissionId);

    const headers = new HttpHeaders();
    headers.append('client_key', this.dstSharedService.clientKey.toString());
    const url = `${this.dstSharedService.baseURLs.efile}/viewStyleSheet`;

    return this.httpClient.get<any>(url, {
      withCredentials: true, params: params, headers: headers
    }).pipe(
      map((data: any) => {
        if (!data.html) {
          throw Error('Something went wrong while getting style sheet, please try again!');
        } else {
          return data.html;
        }
      }),
    );
  }

  getS3Bytes( s3Url: string): Observable<string> {
    const params = new HttpParams()
      .set('fileKey', s3Url.split('/')[s3Url.split('/').length - 1])

    const headers = new HttpHeaders();
    headers.append('client_key', this.dstSharedService.clientKey.toString());
    const url = `${this.dstSharedService.baseURLs.bulkPdfEngine}/getBytesFromS3`;

    return this.httpClient.get(url, {
      withCredentials: true, params: params, headers: headers,responseType:'arraybuffer'
    }).pipe(
      map((data: any) => {
        return data;
      }),
    );
  }

  getDSTTrackerRequirementData(taxYear: string, csId: number, formId: number): Observable<StmtData> {
    const params = new HttpParams({
      fromObject: {
        'tax_year': taxYear,
        'cse_id': csId.toString(),
        'form_id': formId.toString(),
        'reporting_period_id': '12',
      }
    });
    return this.actionService.getActionData(this.dstSharedService.baseURLs.api, 32733, this.dstSharedService.clientKey, params)
      .pipe(
        map((data: ActionResponse) => {
          if (data.callSuccess === '1') {
            try {
              return JSON.parse(data.jsonObject[0].STATEMENTDATA);
            } catch (e: any) {
              if (e instanceof SyntaxError) {
                console.log('JSON parse Syntax Error' + e);
              }else{
                console.error('Unexpected Error' , e);
              }
              throw Error('Something went wrong while retrieving the requrements data, please try again!');
            }
          } else {
            throw Error('Something went wrong while retrieving the requrements data, please try again!');
          }
        })
      );
  }

  loadSecondaryAttachments(statementId: string): void{

    const params = new HttpParams({
      fromObject: {
        'sso_id': this.dstSharedService.user?.ssoId,
        'statement_id':statementId,
      }
    });
    this.actionService.getActionData(this.dstSharedService.baseURLs.api, 33309, this.dstSharedService.clientKey, params)
      .pipe(first())
      .subscribe((data) =>{
         this.secondaryAttachments$.next(data.jsonObject);
      })
  }

  getSecondaryAttachments(): Observable<any[]>{
    return combineLatest([
      this.searchAttachments$,
      this.secondaryAttachments$
    ]).pipe(
        map(([searchQuery, attachments]) => attachments.filter(file => file.FILE_NAME.includes(searchQuery)))
      );
  }

  searchAttachments(searchText: string){
      this.searchAttachments$.next(searchText);
  }

}



export interface DropDownType {
  id: number;
  text: string;
  selected?: boolean;
}

export interface StmtDataType {
  STATEMENTDATA: string;
}

export interface dstLookupEntity {
  entityid: number;
  entity: string;
  leid: string;
  taxYearId: number;
  taxYear: number;
  maintOwner: string;
  ein: string;
  countryCodeInc: string;
  countryInc: string;
  stateCodeInc: string;
  stateInc: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  country: string;
  countryName: string;
  zip: string;
  taxTypeId: string;
  taxTypeCode: string;
  altTaxTypeId: string;
  altTaxTypeCode: string;
  taxType: string;
  altTaxType: string;
  businessId: string;
  business: string;
  geGecsId: string;
}

export interface AppkitDropDownEventType {
  change: DataEntityOrSelectedEntityOrChange;
  items: (ItemsEntity)[];
  selected: (DataEntityOrSelectedEntityOrChange)[];
}

export interface DataEntityOrSelectedEntityOrChange {
  value: number;
  text: string;
  index: number;
  displayInList: boolean;
  selected: boolean;
}

export interface ItemsEntity {
  data?: (DataEntityOrSelectedEntityOrChange)[];
}

export interface EntityNameChange {
  LEID: string;
  LE_NAME?: string;
  EFFECTIVEDATE?: string;
  TAXTYPECODE?: string;
  ALTTAXTYPECODE?: string;
  ALTTAXTYPE?: string;
}
